import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { toArray } from "../lib/api";
import { validators, validateAddress, norm } from "../lib/validation";
import { Plus, Trash2, ChevronDown, ChevronRight, AlertCircle, CheckCircle2 } from "lucide-react";

const emptyAddr = () => ({
  address_type: "residenza", street: "", civic_number: "", zip: "",
  city: "", province: "", region: "", country: "IT", is_primary: true, notes: "",
});

export default function ClientCreatePage() {
  const nav = useNavigate();
  const [tp, setTp] = useState("person");
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [fieldErr, setFieldErr] = useState({});
  const [dup, setDup] = useState(null);
  const [lookups, setLookups] = useState(null);
  const [partners, setPartners] = useState([]);
  const [partnersErr, setPartnersErr] = useState(null);
  const [users, setUsers] = useState([]);
  const [personsForSelect, setPersonsForSelect] = useState([]);

  // Data preserved across tab switches
  const [form, setForm] = useState({
    // shared
    fiscal_code:"", vat_number:"", email:"", pec:"", phone:"", second_phone:"", website:"",
    notes:"", client_status:"lead", acquisition_channel:"",
    referrer_partner_id:"", assigned_user_id:"",
    last_contact_at:"", next_action_at:"", next_action_note:"",
    // privacy
    privacy_consent:false, privacy_consent_date:"", privacy_consent_source:"",
    privacy_revoked_at:"", privacy_notes:"",
    // person
    p_first_name:"", p_last_name:"", p_birth_date:"", p_birth_place:"", p_birth_province:"",
    p_gender:"", p_nationality:"Italiana", p_profession:"",
    p_id_type:"", p_id_number:"", p_id_expiry:"", p_id_notes:"",
    // company
    c_business_name:"", c_legal_form:"", c_incorporation_date:"", c_activity_start_date:"",
    c_ateco:"", c_ateco_secondary:"", c_status:"attiva", c_size:"", c_employees:"",
    c_revenue_last_year:"", c_revenue_year:"", c_rea:"", c_coc:"", c_share_capital:"",
    c_main_contact_subject_id:"", c_legal_rep_subject_id:"",
  });
  const [addresses, setAddresses] = useState([emptyAddr()]);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const loadPartners = () => {
    setPartnersErr(null);
    return api.get("/partners")
      .then(r => setPartners(toArray(r)))
      .catch(() => { setPartners([]); setPartnersErr("Impossibile caricare la lista Partner."); });
  };

  useEffect(() => {
    api.get("/lookups/all").then(r => setLookups(r.data)).catch(()=>{});
    loadPartners();
    api.get("/users").then(r => setUsers(toArray(r))).catch(() => setUsers([]));
    api.get("/subjects?subject_type=person&limit=200").then(r => setPersonsForSelect(toArray(r))).catch(() => setPersonsForSelect([]));
  }, []);

  const errors = useMemo(() => validators(form, tp), [form, tp]);

  const checkDuplicateAsync = async () => {
    const fc = norm.cf(form.fiscal_code);
    const vn = norm.vat(form.vat_number);
    if (!fc && !vn) { setDup(null); return; }
    try {
      const r = await api.get("/subjects/check-duplicates", { params: { fiscal_code: fc, vat_number: vn }});
      setDup(r.data.duplicate);
    } catch { setDup(null); }
  };
  useEffect(() => {
    const t = setTimeout(checkDuplicateAsync, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [form.fiscal_code, form.vat_number]);

  const addAddress = () => setAddresses(a => [...a, { ...emptyAddr(), address_type: tp === "company" ? "sede_legale" : "residenza", is_primary: false }]);
  const rmAddress = (i) => setAddresses(a => a.filter((_, idx) => idx !== i));
  const setAddr = (i, k, v) => setAddresses(a => a.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const setPrimary = (i) => setAddresses(a => a.map((x, idx) => ({ ...x, is_primary: idx === i })));

  const changeTab = (newTp) => {
    if (tp === newTp) return;
    const hasData = (tp === "person" && (form.p_first_name || form.p_last_name)) ||
                    (tp === "company" && form.c_business_name);
    if (hasData) {
      if (!window.confirm("Attenzione: hai già inserito dei dati in questa scheda. Vuoi mantenerli comunque e cambiare tipo di soggetto?")) return;
    }
    setTp(newTp);
    // Reset primary address type default
    setAddresses(list => list.map((a, i) => i === 0
      ? { ...a, address_type: newTp === "company" ? "sede_legale" : "residenza" }
      : a));
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setFieldErr(errors);
    if (Object.keys(errors).length) { setErr("Correggere gli errori evidenziati."); return; }
    if (dup) { setErr(`Duplicato: esiste già "${dup.display_name}". Modificare CF/PIVA o aprire il record esistente.`); return; }
    setBusy(true);
    try {
      const payload = {
        subject_type: tp,
        fiscal_code: norm.cf(form.fiscal_code) || null,
        vat_number: norm.vat(form.vat_number) || null,
        email: form.email || null, pec: form.pec || null,
        phone: form.phone || null, second_phone: form.second_phone || null,
        website: form.website || null, notes: form.notes || null,
        client_status: form.client_status,
        acquisition_channel: form.acquisition_channel || null,
        referrer_partner_id: form.referrer_partner_id || null,
        assigned_user_id: form.assigned_user_id || null,
        privacy_consent: form.privacy_consent,
        privacy_consent_date: form.privacy_consent_date || null,
        privacy_consent_source: form.privacy_consent_source || null,
        privacy_revoked_at: form.privacy_revoked_at || null,
        privacy_notes: form.privacy_notes || null,
        last_contact_at: form.last_contact_at ? new Date(form.last_contact_at).toISOString() : null,
        next_action_at: form.next_action_at ? new Date(form.next_action_at).toISOString() : null,
        next_action_note: form.next_action_note || null,
      };
      if (tp === "person") {
        payload.person = {
          first_name: form.p_first_name.trim(), last_name: form.p_last_name.trim(),
          birth_date: form.p_birth_date || null, birth_place: form.p_birth_place || null,
          birth_province: form.p_birth_province || null, gender: form.p_gender || null,
          nationality: form.p_nationality || null, profession: form.p_profession || null,
          identity_document_type: form.p_id_type || null,
          identity_document_number: form.p_id_number || null,
          identity_document_expiry: form.p_id_expiry || null,
          identity_document_notes: form.p_id_notes || null,
        };
      } else {
        const secList = form.c_ateco_secondary
          ? form.c_ateco_secondary.split(",").map(s => s.trim()).filter(Boolean) : null;
        payload.company = {
          business_name: form.c_business_name.trim(), legal_form: form.c_legal_form || null,
          incorporation_date: form.c_incorporation_date || null,
          activity_start_date: form.c_activity_start_date || null,
          ateco_code: form.c_ateco || null, ateco_secondary: secList,
          company_status: form.c_status || null, company_size: form.c_size || null,
          employees_count: form.c_employees ? parseInt(form.c_employees) : null,
          revenue_last_year: form.c_revenue_last_year ? parseFloat(form.c_revenue_last_year) : null,
          revenue_year: form.c_revenue_year ? parseInt(form.c_revenue_year) : null,
          rea_number: form.c_rea || null, chamber_of_commerce: form.c_coc || null,
          share_capital: form.c_share_capital ? parseFloat(form.c_share_capital) : null,
          main_contact_subject_id: form.c_main_contact_subject_id || null,
          legal_rep_subject_id: form.c_legal_rep_subject_id || null,
        };
      }
      const validAddrs = addresses.filter(a => a.street || a.city || a.zip);
      if (validAddrs.length) {
        // Ensure exactly one primary
        const anyPrim = validAddrs.some(a => a.is_primary);
        if (!anyPrim) validAddrs[0].is_primary = true;
        payload.addresses = validAddrs;
      }
      const r = await api.post("/subjects", payload);
      nav(`/clients/${r.data.id}`);
    } catch (ex) {
      if (ex?.response?.status === 409) {
        const d = ex.response.data?.detail;
        setDup(d?.duplicate || null);
        setErr(d?.message || "Duplicato rilevato");
      } else {
        setErr(ex?.response?.data?.detail || "Errore durante il salvataggio");
      }
    }
    setBusy(false);
  };

  const Err = ({ k }) => fieldErr[k] ? <div className="ma-field-err"><AlertCircle size={12}/> {fieldErr[k]}</div> : null;
  const req = <span className="ma-req">*</span>;

  return (
    <div className="ma-page">
      <h1 className="ma-h1" data-testid="new-client-title">Nuovo cliente</h1>
      <p className="ma-sub-desc">
        Modalità <strong>Salvataggio rapido</strong>: pochi campi essenziali.
        Espandi <em>Dati aggiuntivi</em> per compilare la scheda completa.
        Potrai comunque completare l'anagrafica in seguito.
      </p>

      <div className="ma-tabs">
        <button type="button" className={`ma-tab ${tp==="person"?"active":""}`} onClick={()=>changeTab("person")} data-testid="tab-person">Persona fisica</button>
        <button type="button" className={`ma-tab ${tp==="company"?"active":""}`} onClick={()=>changeTab("company")} data-testid="tab-company">Impresa</button>
      </div>

      <form onSubmit={submit} className="ma-form-panel" noValidate>
        {/* ============ QUICK ============ */}
        <h3 className="ma-h3">Salvataggio rapido</h3>
        {tp === "person" ? (
          <div className="ma-form-grid">
            <div><label className="ma-label">Nome *</label>
              <input className={`ma-input ${fieldErr.p_first_name?"err":""}`} value={form.p_first_name} onChange={e=>set("p_first_name",e.target.value)} data-testid="client-first-name"/>
              <Err k="p_first_name"/></div>
            <div><label className="ma-label">Cognome *</label>
              <input className={`ma-input ${fieldErr.p_last_name?"err":""}`} value={form.p_last_name} onChange={e=>set("p_last_name",e.target.value)} data-testid="client-last-name"/>
              <Err k="p_last_name"/></div>
            <div><label className="ma-label">Codice fiscale</label>
              <input className={`ma-input ma-mono ${fieldErr.fiscal_code?"err":""}`} value={form.fiscal_code} onChange={e=>set("fiscal_code",e.target.value.toUpperCase())} data-testid="client-tax-code"/>
              <Err k="fiscal_code"/></div>
            <div><label className="ma-label">Telefono</label>
              <input className="ma-input" value={form.phone} onChange={e=>set("phone",e.target.value)}/></div>
            <div className="ma-col-2"><label className="ma-label">Email</label>
              <input type="email" className={`ma-input ${fieldErr.email?"err":""}`} value={form.email} onChange={e=>set("email",e.target.value)}/>
              <Err k="email"/></div>
          </div>
        ) : (
          <div className="ma-form-grid">
            <div className="ma-col-2"><label className="ma-label">Denominazione / Ragione sociale *</label>
              <input className={`ma-input ${fieldErr.c_business_name?"err":""}`} value={form.c_business_name} onChange={e=>set("c_business_name",e.target.value)} data-testid="client-business-name"/>
              <Err k="c_business_name"/></div>
            <div><label className="ma-label">Partita IVA</label>
              <input className={`ma-input ma-mono ${fieldErr.vat_number?"err":""}`} value={form.vat_number} onChange={e=>set("vat_number",e.target.value)} data-testid="client-vat-number"/>
              <Err k="vat_number"/></div>
            <div><label className="ma-label">Codice fiscale</label>
              <input className={`ma-input ma-mono ${fieldErr.fiscal_code?"err":""}`} value={form.fiscal_code} onChange={e=>set("fiscal_code",e.target.value.toUpperCase())} data-testid="fiscal-code"/>
              <Err k="fiscal_code"/></div>
            <div><label className="ma-label">Telefono</label>
              <input className="ma-input" value={form.phone} onChange={e=>set("phone",e.target.value)}/></div>
            <div><label className="ma-label">Email</label>
              <input type="email" className={`ma-input ${fieldErr.email?"err":""}`} value={form.email} onChange={e=>set("email",e.target.value)}/>
              <Err k="email"/></div>
          </div>
        )}

        {dup && (
          <div className="ma-warning" data-testid="dup-warning">
            <AlertCircle size={16}/>
            <span>Esiste già un soggetto con questo CF/PIVA: <strong>{dup.display_name}</strong> — <a href={`/clients/${dup.id}`} target="_blank" rel="noreferrer" className="ma-link">Apri</a></span>
          </div>
        )}

        {/* Primary address in quick mode */}
        <h3 className="ma-h3">Indirizzo principale</h3>
        <AddressRow addr={addresses[0] || emptyAddr()} onChange={(k,v)=>setAddr(0,k,v)} lookups={lookups} tp={tp} isPrimaryHint />

        <button type="button" className="ma-toggle" onClick={()=>setExpanded(v=>!v)} data-testid="toggle-full-form">
          {expanded ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
          {expanded ? "Nascondi" : "Mostra"} dati aggiuntivi
        </button>

        {/* ============ FULL FORM ============ */}
        {expanded && (
          <>
            {tp === "person" ? (
              <>
                <h3 className="ma-h3">Dati anagrafici</h3>
                <div className="ma-form-grid">
                  <div><label className="ma-label">Data di nascita</label>
                    <input type="date" className={`ma-input ${fieldErr.p_birth_date?"err":""}`} value={form.p_birth_date} onChange={e=>set("p_birth_date",e.target.value)}/>
                    <Err k="p_birth_date"/></div>
                  <div><label className="ma-label">Luogo di nascita</label>
                    <input className="ma-input" value={form.p_birth_place} onChange={e=>set("p_birth_place",e.target.value)}/></div>
                  <div><label className="ma-label">Prov. o Stato estero</label>
                    <input className="ma-input" value={form.p_birth_province} onChange={e=>set("p_birth_province",e.target.value)}/></div>
                  <div><label className="ma-label">Sesso</label>
                    <select className="ma-input" value={form.p_gender} onChange={e=>set("p_gender",e.target.value)}>
                      <option value="">—</option><option value="M">Maschio</option><option value="F">Femmina</option><option value="X">Non specificato</option>
                    </select></div>
                  <div><label className="ma-label">Nazionalità</label>
                    <input className="ma-input" value={form.p_nationality} onChange={e=>set("p_nationality",e.target.value)}/></div>
                  <div><label className="ma-label">Professione</label>
                    <input className="ma-input" value={form.p_profession} onChange={e=>set("p_profession",e.target.value)}/></div>
                </div>
                <h3 className="ma-h3">Documento di identità</h3>
                <div className="ma-form-grid">
                  <div><label className="ma-label">Tipo documento</label>
                    <select className="ma-input" value={form.p_id_type} onChange={e=>set("p_id_type",e.target.value)}>
                      <option value="">—</option>
                      {lookups?.identity_document_types?.map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
                    </select></div>
                  <div><label className="ma-label">Numero documento</label>
                    <input className="ma-input" value={form.p_id_number} onChange={e=>set("p_id_number",e.target.value)}/></div>
                  <div><label className="ma-label">Scadenza</label>
                    <input type="date" className="ma-input" value={form.p_id_expiry} onChange={e=>set("p_id_expiry",e.target.value)}/></div>
                  <div className="ma-col-full"><label className="ma-label">Note documento</label>
                    <input className="ma-input" value={form.p_id_notes} onChange={e=>set("p_id_notes",e.target.value)}/></div>
                </div>
              </>
            ) : (
              <>
                <h3 className="ma-h3">Dati impresa</h3>
                <div className="ma-form-grid">
                  <div><label className="ma-label">Forma giuridica</label>
                    <input className="ma-input" placeholder="SRL, SPA, SNC…" value={form.c_legal_form} onChange={e=>set("c_legal_form",e.target.value)}/></div>
                  <div><label className="ma-label">Data costituzione</label>
                    <input type="date" className={`ma-input ${fieldErr.c_incorporation_date?"err":""}`} value={form.c_incorporation_date} onChange={e=>set("c_incorporation_date",e.target.value)}/>
                    <Err k="c_incorporation_date"/></div>
                  <div><label className="ma-label">Inizio attività</label>
                    <input type="date" className="ma-input" value={form.c_activity_start_date} onChange={e=>set("c_activity_start_date",e.target.value)}/></div>
                  <div><label className="ma-label">Stato impresa</label>
                    <select className="ma-input" value={form.c_status} onChange={e=>set("c_status",e.target.value)}>
                      <option value="">—</option>
                      {lookups?.company_statuses?.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
                    </select></div>
                  <div><label className="ma-label">ATECO principale</label>
                    <input className="ma-input ma-mono" value={form.c_ateco} onChange={e=>set("c_ateco",e.target.value)}/></div>
                  <div><label className="ma-label">ATECO secondari (virgola)</label>
                    <input className="ma-input ma-mono" placeholder="es. 62.01, 62.02" value={form.c_ateco_secondary} onChange={e=>set("c_ateco_secondary",e.target.value)}/></div>
                  <div><label className="ma-label">Dimensione</label>
                    <select className="ma-input" value={form.c_size} onChange={e=>set("c_size",e.target.value)}>
                      <option value="">—</option>
                      <option value="micro">Micro</option><option value="small">Piccola</option>
                      <option value="medium">Media</option><option value="large">Grande</option>
                    </select></div>
                  <div><label className="ma-label">Dipendenti</label>
                    <input type="number" className={`ma-input ${fieldErr.c_employees_count?"err":""}`} value={form.c_employees} onChange={e=>set("c_employees",e.target.value)}/></div>
                  <div><label className="ma-label">Numero REA</label>
                    <input className="ma-input" value={form.c_rea} onChange={e=>set("c_rea",e.target.value)}/></div>
                  <div><label className="ma-label">Camera di commercio</label>
                    <input className="ma-input" value={form.c_coc} onChange={e=>set("c_coc",e.target.value)}/></div>
                  <div><label className="ma-label">Capitale sociale (€)</label>
                    <input type="number" step="0.01" className={`ma-input ${fieldErr.c_share_capital?"err":""}`} value={form.c_share_capital} onChange={e=>set("c_share_capital",e.target.value)}/>
                    <Err k="c_share_capital"/></div>
                  <div><label className="ma-label">Fatturato ultimo esercizio (€)</label>
                    <input type="number" step="0.01" className={`ma-input ${fieldErr.c_revenue_last_year?"err":""}`} value={form.c_revenue_last_year} onChange={e=>set("c_revenue_last_year",e.target.value)}/>
                    <Err k="c_revenue_last_year"/></div>
                  <div><label className="ma-label">Anno di riferimento</label>
                    <input type="number" className="ma-input" value={form.c_revenue_year} onChange={e=>set("c_revenue_year",e.target.value)}/></div>
                </div>
                <h3 className="ma-h3">Riferimenti persone</h3>
                <div className="ma-form-grid">
                  <div><label className="ma-label">Referente principale (persona fisica)</label>
                    <select className="ma-input" value={form.c_main_contact_subject_id} onChange={e=>set("c_main_contact_subject_id",e.target.value)} data-testid="c-main-contact">
                      <option value="">— Seleziona persona —</option>
                      {personsForSelect.map(p => <option key={p.id} value={p.id}>{p.display_name}</option>)}
                    </select>
                    <div className="ma-hint">Puoi anche crearlo dopo dalle Relazioni</div></div>
                  <div><label className="ma-label">Legale rappresentante</label>
                    <select className="ma-input" value={form.c_legal_rep_subject_id} onChange={e=>set("c_legal_rep_subject_id",e.target.value)} data-testid="c-legal-rep">
                      <option value="">— Seleziona persona —</option>
                      {personsForSelect.map(p => <option key={p.id} value={p.id}>{p.display_name}</option>)}
                    </select></div>
                </div>
              </>
            )}

            <h3 className="ma-h3">Contatti aggiuntivi</h3>
            <div className="ma-form-grid">
              <div><label className="ma-label">PEC</label>
                <input type="email" className={`ma-input ${fieldErr.pec?"err":""}`} value={form.pec} onChange={e=>set("pec",e.target.value)}/>
                <Err k="pec"/></div>
              <div><label className="ma-label">Telefono secondario</label>
                <input className="ma-input" value={form.second_phone} onChange={e=>set("second_phone",e.target.value)}/></div>
              <div className="ma-col-2"><label className="ma-label">Sito web</label>
                <input className="ma-input" value={form.website} onChange={e=>set("website",e.target.value)}/></div>
            </div>

            <h3 className="ma-h3">Indirizzi aggiuntivi</h3>
            {addresses.slice(1).map((a, i) => (
              <div key={i+1} className="ma-addr-row">
                <AddressRow addr={a} onChange={(k,v)=>setAddr(i+1,k,v)} lookups={lookups} tp={tp}/>
                <div className="ma-addr-actions">
                  <label className="ma-checkbox"><input type="checkbox" checked={a.is_primary} onChange={()=>setPrimary(i+1)}/> Principale</label>
                  <button type="button" className="ma-btn-danger-sm" onClick={()=>rmAddress(i+1)}><Trash2 size={12}/></button>
                </div>
              </div>
            ))}
            <button type="button" className="ma-btn-secondary" onClick={addAddress}><Plus size={14}/> Aggiungi indirizzo</button>

            <h3 className="ma-h3">Gestione commerciale</h3>
            <div className="ma-form-grid">
              <div><label className="ma-label">Stato cliente</label>
                <select className="ma-input" value={form.client_status} onChange={e=>set("client_status",e.target.value)}>
                  {lookups?.client_statuses?.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
                </select></div>
              <div><label className="ma-label">Canale di acquisizione</label>
                <select className="ma-input" value={form.acquisition_channel} onChange={e=>set("acquisition_channel",e.target.value)} data-testid="acquisition-channel">
                  <option value="">—</option>
                  {lookups?.acquisition_channels?.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                </select></div>
              <div><label className="ma-label">Partner segnalatore</label>
                <select className="ma-input" value={form.referrer_partner_id} onChange={e=>set("referrer_partner_id",e.target.value)} data-testid="referrer-partner">
                  <option value="">— {partnersErr ? "Errore caricamento" : "Nessuno"} —</option>
                  {(Array.isArray(partners) ? partners : []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                {partnersErr && (
                  <div className="ma-notice" data-testid="partners-err" style={{marginTop:6,display:"flex",alignItems:"center",gap:8}}>
                    <span>{partnersErr}</span>
                    <button type="button" className="ma-btn-link" onClick={loadPartners} data-testid="partners-retry">Riprova</button>
                  </div>
                )}
              </div>
              <div><label className="ma-label">Utente incaricato</label>
                <select className="ma-input" value={form.assigned_user_id} onChange={e=>set("assigned_user_id",e.target.value)} data-testid="assigned-user">
                  <option value="">—</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                </select></div>
              <div><label className="ma-label">Ultimo contatto</label>
                <input type="datetime-local" className="ma-input" value={form.last_contact_at} onChange={e=>set("last_contact_at",e.target.value)}/></div>
              <div><label className="ma-label">Prossima azione</label>
                <input type="datetime-local" className="ma-input" value={form.next_action_at} onChange={e=>set("next_action_at",e.target.value)}/></div>
              <div className="ma-col-full"><label className="ma-label">Descrizione prossima azione</label>
                <input className="ma-input" value={form.next_action_note} onChange={e=>set("next_action_note",e.target.value)}/></div>
            </div>

            <h3 className="ma-h3">Privacy</h3>
            <div className="ma-form-grid">
              <div className="ma-col-2">
                <label className="ma-checkbox">
                  <input type="checkbox" checked={form.privacy_consent} onChange={e=>set("privacy_consent",e.target.checked)} data-testid="privacy-consent"/>
                  <span>{form.privacy_consent ? <CheckCircle2 size={14} color="#2E7D32"/> : <AlertCircle size={14} color="#F59E0B"/>} Consenso privacy acquisito</span>
                </label>
                <div className="ma-hint">Non obbligatorio per salvare, ma raccomandato prima di lavorare i dati.</div>
              </div>
              <div><label className="ma-label">Data consenso</label>
                <input type="date" className="ma-input" value={form.privacy_consent_date} onChange={e=>set("privacy_consent_date",e.target.value)}/></div>
              <div><label className="ma-label">Modalità / fonte</label>
                <input className="ma-input" placeholder="es. modulo cartaceo, email, incontro" value={form.privacy_consent_source} onChange={e=>set("privacy_consent_source",e.target.value)}/></div>
              <div><label className="ma-label">Data revoca (eventuale)</label>
                <input type="date" className="ma-input" value={form.privacy_revoked_at} onChange={e=>set("privacy_revoked_at",e.target.value)}/></div>
              <div className="ma-col-full"><label className="ma-label">Note privacy</label>
                <textarea className="ma-input" rows={2} value={form.privacy_notes} onChange={e=>set("privacy_notes",e.target.value)}></textarea></div>
            </div>

            <h3 className="ma-h3">Note</h3>
            <label className="ma-label" htmlFor="notes-field">Note generali</label>
            <textarea id="notes-field" className="ma-input" rows={4} value={form.notes} onChange={e=>set("notes",e.target.value)} data-testid="notes-field"/>
          </>
        )}

        {err && <div className="ma-error" data-testid="submit-error">{err}</div>}
        <div className="ma-form-actions">
          <button type="button" className="ma-btn-secondary" onClick={()=>nav(-1)}>Annulla</button>
          <button type="submit" className="ma-btn-primary" disabled={busy} data-testid="submit-new-client">
            {busy ? "Salvataggio…" : "Crea cliente"}
          </button>
        </div>
      </form>
    </div>
  );
}

function AddressRow({ addr, onChange, lookups, tp, isPrimaryHint }) {
  const [aErr, setAErr] = useState({});
  useEffect(() => { setAErr(validateAddress(addr)); }, [addr]);
  const types = lookups?.address_types || [];
  return (
    <div className="ma-form-grid">
      <div><label className="ma-label">Tipo</label>
        <select className="ma-input" value={addr.address_type} onChange={e=>onChange("address_type",e.target.value)} data-testid="addr-type">
          {types.map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
        </select></div>
      <div className="ma-col-2"><label className="ma-label">Via / Piazza</label>
        <input className="ma-input" value={addr.street} onChange={e=>onChange("street",e.target.value)} data-testid="addr-street"/></div>
      <div><label className="ma-label">Civico</label>
        <input className="ma-input" value={addr.civic_number} onChange={e=>onChange("civic_number",e.target.value)}/></div>
      <div><label className="ma-label">CAP</label>
        <input className={`ma-input ${aErr.zip?"err":""}`} value={addr.zip} onChange={e=>onChange("zip",e.target.value)} data-testid="addr-zip"/>
        {aErr.zip && <div className="ma-field-err">{aErr.zip}</div>}</div>
      <div><label className="ma-label">Comune</label>
        <input className="ma-input" value={addr.city} onChange={e=>onChange("city",e.target.value)} data-testid="addr-city"/></div>
      <div><label className="ma-label">Provincia</label>
        <input className="ma-input" maxLength={2} value={addr.province} onChange={e=>onChange("province",e.target.value.toUpperCase().slice(0,2))}/></div>
      <div><label className="ma-label">Regione</label>
        <input className="ma-input" value={addr.region} onChange={e=>onChange("region",e.target.value)}/></div>
      <div><label className="ma-label">Stato</label>
        <input className="ma-input" value={addr.country} onChange={e=>onChange("country",e.target.value.toUpperCase())}/></div>
      {isPrimaryHint && <div className="ma-col-full"><div className="ma-hint">Questo è l'indirizzo principale. Altri indirizzi possono essere aggiunti in "Dati aggiuntivi".</div></div>}
    </div>
  );
}
